import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, Search, MessageCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const FAQ: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const categories = [
    {
      title: 'Getting Started',
      questions: [
        {
          id: 1,
          question: 'How do I create an account?',
          answer: 'Creating an account is simple. Click the "Sign Up" button, enter your email address, and follow the verification process. Once verified, you can complete your profile and start exploring career opportunities.'
        },
        {
          id: 2,
          question: 'Is the service free to use?',
          answer: 'We offer both free and premium plans. The free plan includes basic features like career assessments and basic recommendations. Premium plans offer additional features such as mentorship, detailed insights, and priority support.'
        },
        {
          id: 3,
          question: 'How do I get started with career guidance?',
          answer: 'After creating an account, take our comprehensive career assessment. Based on your responses, we\'ll provide personalized career recommendations and a roadmap for skill development.'
        }
      ]
    },
    {
      title: 'Career Assessment',
      questions: [
        {
          id: 4,
          question: 'How long does the career assessment take?',
          answer: 'The initial career assessment typically takes 15-20 minutes to complete. You can save your progress and return later if needed.'
        },
        {
          id: 5,
          question: 'Can I retake the assessment?',
          answer: 'Yes, you can retake the assessment at any time. We recommend retaking it every 6-12 months or when your career goals significantly change.'
        },
        {
          id: 6,
          question: 'How accurate are the career recommendations?',
          answer: 'Our recommendations are based on advanced algorithms analyzing your skills, interests, and market trends. While highly accurate, they should be used as guidance alongside your personal judgment.'
        }
      ]
    },
    {
      title: 'Mentorship',
      questions: [
        {
          id: 7,
          question: 'How does mentorship work?',
          answer: 'Our mentorship program connects you with experienced professionals in your field of interest. You can schedule one-on-one sessions, get career advice, and receive guidance on skill development.'
        },
        {
          id: 8,
          question: 'How are mentors selected?',
          answer: 'Mentors go through a rigorous verification process. We check their professional experience, expertise, and commitment to mentoring before they can join our platform.'
        },
        {
          id: 9,
          question: 'Can I change my mentor?',
          answer: 'Yes, you can change your mentor at any time. We encourage finding the right mentor match for your career goals and learning style.'
        }
      ]
    },
    {
      title: 'Technical Support',
      questions: [
        {
          id: 10,
          question: 'What if I encounter technical issues?',
          answer: 'For technical support, visit our Help Center or contact our support team. We typically respond within 24 hours on business days.'
        },
        {
          id: 11,
          question: 'How do I reset my password?',
          answer: 'Click the "Forgot Password" link on the login page. We\'ll send you instructions to reset your password via email.'
        },
        {
          id: 12,
          question: 'Is my data secure?',
          answer: 'Yes, we use industry-standard encryption and security measures to protect your data. Read our Privacy Policy for more details.'
        }
      ]
    }
  ];

  const filteredQuestions = categories.map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <main className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-600 mb-8">
              Find answers to common questions about Career Guidance+
            </p>

            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-12 py-4 text-lg w-full"
              />
            </div>
          </div>

          <div className="space-y-8">
            {filteredQuestions.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <h2 className="text-xl font-bold text-gray-900 p-6 bg-gray-50">
                  {category.title}
                </h2>
                <div className="divide-y divide-gray-200">
                  {category.questions.map((item) => (
                    <div key={item.id} className="p-6">
                      <button
                        onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                        className="flex justify-between items-start w-full text-left"
                      >
                        <h3 className="text-lg font-medium text-gray-900 pr-8">
                          {item.question}
                        </h3>
                        {expandedId === item.id ? (
                          <ChevronUp className="h-6 w-6 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-6 w-6 text-gray-400" />
                        )}
                      </button>
                      {expandedId === item.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-4"
                        >
                          <p className="text-gray-600">
                            {item.answer}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Still Have Questions Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 bg-primary-900 text-white rounded-lg p-8 text-center"
          >
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-primary-100 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <Button
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white hover:text-primary-900"
              icon={<MessageCircle className="h-4 w-4" />}
            >
              Contact Support
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
};

export default FAQ;